import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import FilterListIcon from "@mui/icons-material/FilterList";
// import RefreshIcon from "@mui/icons-material/Refresh";
import { Box, capitalize, Chip, CircularProgress, Divider, Grid, Stack, Tab, Tabs, Typography } from "@mui/material";
import { MRT_ColumnDef, MRT_ColumnFiltersState } from "material-react-table";
import moment from "moment";
import React, { useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";
import {
  convertHighlightToDataByTab,
  EarlyPaymentData,
  EarlyPaymentStatus,
  Highlight,
  InitiationDayFilter,
  PaymentStatus,
  StatCard,
} from "../AnchorNew/AnchorDashboard";
import AnchorDashboardFilter from "../AnchorNew/AnchorDashboardFilter";
import CustomButton from "../Common/CustomButton";
import EllipsisedCell, { formatNumberAndCurrency } from "../Common/EllipsisedCell";
import FiltersBars from "../Common/FiltersBars";
import { LoggedInSkeleton } from "../Common/LoggedInSkeleton";
import { getReconTopBarButtons } from "../Common/TopNavBar";
import useFetch from "../Common/useFetch";
import { userContext } from "../Contexts/userContext";
import ReactTable from "../ReactTable";
import BulkActionsDialog from "./BulkActionsDialog";
import { downloadExcelFromBuffer } from "./UploadFileCommonDialog";
import { datefilterFn } from "./UploadInvoice";

const tabLabels = ["Daily early payments", "Previous early payments"];

export type AllowedFilterKeysDailyPayments =
  | "vendorName"
  | "panNumber"
  | "grnNumber"
  | "invoiceNumber"
  | "warehouseName"
  | "earlyPayPass";

export type AllowedFilterKeysPreviousPayments =
  | "vendorName"
  | "panNumber"
  | "grnNumber"
  | "invoiceNumber"
  | "warehouseName"
  | "earlyPayDate"
  | "cnStatus"
  | "dnAdjusted"
  | "nakadCnReceived"
  | "anchorCnReceived";

const OperationsPage = () => {
  const { actor } = useContext(userContext);
  const location = useLocation<any>();
  const [tabIndex, setTabIndex] = useState(0);
  const [selectedTime, setSelectedTime] = useState<InitiationDayFilter>(InitiationDayFilter.ALL);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [earlyPaymentData, setEarlyPaymentData] = useState<EarlyPaymentData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRow, setSelectedRow] = useState<EarlyPaymentData[]>([]);
  const [clearRows, setClearRows] = useState(false);
  const [highlights, setHighlights] = useState<Highlight>({
    earlyPaidInvoiceCount: 0,
    earlyPaidInvoiceValue: 0,
    totalDisbursedAmount: 0,
    incomeAccrued: 0,
    currentAUM: 0,
    totalDiscountAmount: 0,
  });
  const [openFilter, setOpenFilter] = useState<boolean>(false);

  const [appliedFiltersDailyPayments, setAppliedFiltersDailyPayments] = useState<
    Record<AllowedFilterKeysDailyPayments, string[]>
  >({
    vendorName: [],
    panNumber: [],
    grnNumber: [],
    invoiceNumber: [],
    warehouseName: [],
    earlyPayPass: [],
  });
  const [appliedFiltersPreviousPayments, setAppliedFiltersPreviousPayments] = useState<
    Record<AllowedFilterKeysPreviousPayments, string[]>
  >({
    vendorName: [],
    panNumber: [],
    grnNumber: [],
    invoiceNumber: [],
    earlyPayDate: [],
    warehouseName: [],
    cnStatus: [],
    dnAdjusted: [],
    nakadCnReceived: [],
    anchorCnReceived: [],
  });
  const [isDownloadingReport, setIsDownloadingReport] = useState<boolean>(false);
  const [columnFiltersDailyPayments, setColumnFiltersDailyPayments] = useState<MRT_ColumnFiltersState>([]);
  const [columnFiltersPreviousPayments, setColumnFiltersPreviousPayments] = useState<MRT_ColumnFiltersState>([]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const handleTimeFilter = (label: InitiationDayFilter) => {
    setSelectedTime(label);
  };

  useEffect(() => {
    // Fetch daily payments data when the component mounts
    getDailyPaymentsData();
  }, [tabIndex, appliedFiltersDailyPayments, appliedFiltersPreviousPayments, selectedTime]);

  const getDailyPaymentsData = () => {
    setIsLoading(true);
    const startDate = appliedFiltersPreviousPayments.earlyPayDate[0]
      ? moment(appliedFiltersPreviousPayments.earlyPayDate[0]).format("DD-MM-YYYY")
      : null;
    const endDate = appliedFiltersPreviousPayments.earlyPayDate[1]
      ? moment(appliedFiltersPreviousPayments.earlyPayDate[1]).format("DD-MM-YYYY")
      : null;
    const filterObject =
      tabIndex === 0
        ? {
            vendorName: appliedFiltersDailyPayments.vendorName,
            pan: appliedFiltersDailyPayments.panNumber,
            grnNumber: appliedFiltersDailyPayments.grnNumber,
            invoiceNumber: appliedFiltersDailyPayments.invoiceNumber,
            warehouseName: appliedFiltersDailyPayments.warehouseName,
            earlyPayPass: appliedFiltersDailyPayments.earlyPayPass,
            initiationDay: selectedTime,
          }
        : {
            vendorName: appliedFiltersPreviousPayments.vendorName,
            pan: appliedFiltersPreviousPayments.panNumber,
            grnNumber: appliedFiltersPreviousPayments.grnNumber,
            invoiceNumber: appliedFiltersPreviousPayments.invoiceNumber,
            earlyPayStartDate: startDate,
            earlyPayEndDate: endDate,
            initiationDay: selectedTime,
            warehouseName: appliedFiltersPreviousPayments.warehouseName,
            cnStatus: appliedFiltersPreviousPayments.cnStatus,
            dnAdjusted: appliedFiltersPreviousPayments.dnAdjusted,
            nakadCnReceived: appliedFiltersPreviousPayments.nakadCnReceived,
            anchorCnReceived: appliedFiltersPreviousPayments.anchorCnReceived,
          };

    const url = tabIndex === 0 ? "/invoices/early-payment/2" : "/invoices/previous-payment/2";
    useFetch(url, "POST", {
      failureMessage: "Failed to fetch daily early payments data",
      data: filterObject,
      thenCallBack: (response) => {
        setEarlyPaymentData(response.data.result.invoices);
        setHighlights(response.data.result.highlights);
        setIsLoading(false);
      },
      catchCallBack: () => {
        setIsLoading(false);
      },
    });
  };

  const handleDownloadReport = async () => {
    setIsDownloadingReport(true);

    const startDate = appliedFiltersPreviousPayments.earlyPayDate[0]
      ? moment(appliedFiltersPreviousPayments.earlyPayDate[0]).format("DD-MM-YYYY")
      : null;
    const endDate = appliedFiltersPreviousPayments.earlyPayDate[1]
      ? moment(appliedFiltersPreviousPayments.earlyPayDate[1]).format("DD-MM-YYYY")
      : null;

    const url = tabIndex === 0 ? "invoices/early-payment/2/report" : "invoices/previous-payment/2/report";
    const reportName = tabIndex === 0 ? "Early Payment report.xlsx" : "Previous Payment report.xlsx";
    const reqBody =
      tabIndex === 0
        ? {
            vendorName: appliedFiltersDailyPayments.vendorName || [],
            pan: appliedFiltersDailyPayments.panNumber || [],
            grnNumber: appliedFiltersDailyPayments.grnNumber || [],
            invoiceNumber: appliedFiltersDailyPayments.invoiceNumber || [],
            warehouseName: appliedFiltersDailyPayments.warehouseName || [],
            earlyPayPass: appliedFiltersDailyPayments.earlyPayPass || [],
            initiationDay: selectedTime,
          }
        : {
            vendorName: appliedFiltersPreviousPayments.vendorName || [],
            pan: appliedFiltersPreviousPayments.panNumber || [],
            grnNumber: appliedFiltersPreviousPayments.grnNumber || [],
            invoiceNumber: appliedFiltersPreviousPayments.invoiceNumber || [],
            earlyPayStartDate: startDate,
            earlyPayEndDate: endDate,
            initiationDay: selectedTime,
            warehouseName: appliedFiltersPreviousPayments.warehouseName || [],
            cnStatus: appliedFiltersPreviousPayments.cnStatus || [],
            dnAdjusted: appliedFiltersPreviousPayments.dnAdjusted || [],
            nakadCnReceived: appliedFiltersPreviousPayments.nakadCnReceived || [],
            anchorCnReceived: appliedFiltersPreviousPayments.anchorCnReceived || [],
          };

    await useFetch(url, "POST", {
      failureMessage: "Failed to download report",
      data: reqBody,
      thenCallBack: (response) => {
        const reportBuffer =
          tabIndex === 0
            ? response.data.result.earlyPaymentsReport.data
            : response.data.result.previousPaymentsReport.data;

        downloadExcelFromBuffer(reportBuffer, reportName);
        setIsDownloadingReport(false);
      },
      catchCallBack: () => {
        setIsDownloadingReport(false);
      },
    });
  };

  const handleDownloadCN = async (invoiceId: string) => {
    await useFetch(`/invoices/credit-note/download/${invoiceId}`, "GET", {
      failureMessage: "Failed to download Credit Note",
      thenCallBack: (response) => {
        downloadExcelFromBuffer(response.data.result.creditNoteFile, response.data.result.fileName);
      },
    });
  };

  const columnsDailyPayments = useMemo<MRT_ColumnDef<EarlyPaymentData>[]>(
    () => [
      {
        header: "Vendor Name",
        accessorKey: "vendorName",
        minSize: 250,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "PAN",
        accessorKey: "pan",
        minSize: 150,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "GRN Date",
        accessorKey: "grnDate",
        size: 350,
        filterVariant: "date-range",
        filterFn: (row, _id, filterValue) => {
          return datefilterFn(row.original.grnDate, filterValue);
        },
        accessorFn: (row) => (row.grnDate ? new Date(row.grnDate) : null),
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="date" />,
      },
      {
        header: "GRN Amount",
        accessorKey: "grnAmount",
        minSize: 250,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },

      {
        header: "Payment status",
        accessorKey: "paymentStatus",
        filterVariant: "multi-select",
        minSize: 300,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<EarlyPaymentStatus>()} type="text" />,
      },
      {
        header: "Hold-off amt",
        accessorKey: "holdOffAmount",
        minSize: 250,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Adjustments",
        accessorKey: "finalAdjustments",
        minSize: 250,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Outstanding (NAKAD)",
        accessorKey: "outstandingAmountNakad",
        minSize: 250,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Starting CB",
        accessorKey: "startingCB",
        minSize: 250,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Effective CB",
        accessorKey: "effectiveCB",
        minSize: 250,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Gross Payment Amt",
        accessorKey: "grossPaymentAmount",
        minSize: 250,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Payment to be made",
        accessorKey: "amountDisbursed",
        minSize: 250,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<number>()} type="accounting" />,
      },

      {
        header: "Vendor CN Date",
        accessorKey: "vendorCnDate",
        size: 350,
        filterVariant: "date-range",
        filterFn: (row, _id, filterValue) => {
          return datefilterFn(row.original.vendorCnDate, filterValue);
        },
        accessorFn: (row) => (row.vendorCnDate ? new Date(row.vendorCnDate) : null),
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="date" />,
      },

      {
        header: "Vendor CN Number",
        accessorKey: "vendorCnNumber",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "Discount Amount",
        accessorKey: "discountAmount",
        minSize: 250,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Early pay pass",
        accessorKey: "earlyPayPass",
        filterVariant: "multi-select",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },

      {
        header: "Initiation Date",
        accessorKey: "initiationDate",
        size: 350,
        filterVariant: "date-range",
        filterFn: (row, _id, filterValue) => {
          return datefilterFn(row.original.initiationDate, filterValue);
        },
        accessorFn: (row) => (row.initiationDate ? new Date(row.initiationDate) : null),
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="date" />,
      },
      {
        header: "Early pay date",
        accessorKey: "earlyPayDate",
        size: 350,
        filterVariant: "date-range",
        filterFn: (row, _id, filterValue) => {
          return datefilterFn(row.original.earlyPayDate, filterValue);
        },
        accessorFn: (row) => (row.earlyPayDate ? new Date(row.earlyPayDate) : null),
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="date" />,
      },
      {
        header: "Discounting Status",
        accessorKey: "discountingStatus",
        filterVariant: "multi-select",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "Organization Name",
        accessorKey: "entityName",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "GRN Number",
        accessorKey: "grnNumber",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "Warehouse Name",
        accessorKey: "warehouseName",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "Creator Invoice Date",
        accessorKey: "creatorInvoiceDate",
        size: 250,
        accessorFn: (row) => (row.creatorInvoiceDate ? moment(row.creatorInvoiceDate).format("DD/MM/YYYY") : null),
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "Invoice Number",
        accessorKey: "invoiceNumber",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "Invoice Accounting Date",
        accessorKey: "invoiceAccountDate",
        size: 350,
        filterVariant: "date-range",
        filterFn: (row, _id, filterValue) => {
          return datefilterFn(row.original.invoiceAccountDate, filterValue);
        },
        accessorFn: (row) => (row.invoiceAccountDate ? new Date(row.invoiceAccountDate) : null),
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="date" />,
      },
      {
        header: "Outstanding payment",
        accessorKey: "outstandingAmount",
        minSize: 250,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<number>()} type="accounting" />,
      },
      {
        header: "Due Date",
        accessorKey: "dueDate",
        size: 350,
        filterVariant: "date-range",
        filterFn: (row, _id, filterValue) => {
          return datefilterFn(row.original.dueDate, filterValue);
        },
        accessorFn: (row) => (row.dueDate ? new Date(row.dueDate) : null),
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="date" />,
      },
      {
        header: "Credit Note Number",
        accessorKey: "creditNoteNumber",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "GSTIN",
        accessorKey: "gstin",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "Status of Invoice",
        accessorKey: "invoicePostedStatus",
        filterVariant: "multi-select",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "Invoice amount",
        accessorKey: "invoiceAmount",
        minSize: 250,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Credit Period",
        accessorKey: "creditPeriod",
        minSize: 250,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="number" />,
      },
      {
        header: "Balance amount to be paid on due date",
        accessorKey: "balanceAmountToBePaidOnDueDate",
        minSize: 420,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Vendor Code",
        accessorKey: "vendorCode",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "City",
        accessorKey: "city",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "State",
        accessorKey: "state",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "PO No.",
        accessorKey: "poNumber",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "PO Date",
        accessorKey: "poDate",
        size: 350,
        filterVariant: "date-range",
        filterFn: (row, _id, filterValue) => {
          return datefilterFn(row.original.poDate, filterValue);
        },
        accessorFn: (row) => (row.poDate ? new Date(row.poDate) : null),
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="date" />,
      },
      {
        header: "PO Amount",
        accessorKey: "poAmount",
        minSize: 250,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "TDS/TCS",
        accessorKey: "tds",
        minSize: 250,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Purchase Return Amount",
        accessorKey: "purchaseReturnAmount",
        size: 300,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Brand discount (Promo Claims)",
        accessorKey: "brandDiscount",
        size: 350,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Other Debit Amount",
        accessorKey: "debitAmount",
        minSize: 250,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Other adjustments",
        accessorKey: "adjustments",
        minSize: 250,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Net Payable Amount",
        accessorKey: "netPayableAmount",
        minSize: 250,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Other Adjustment Date",
        accessorKey: "otherAdjustmentDate",
        size: 350,
        filterVariant: "date-range",
        filterFn: (row, _id, filterValue) => {
          return datefilterFn(row.original.otherAdjustmentDate, filterValue);
        },
        accessorFn: (row) => (row.otherAdjustmentDate ? new Date(row.otherAdjustmentDate) : null),
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="date" />,
      },
      {
        header: "Invoice Status",
        accessorKey: "invoiceReconciledStatus",
        filterVariant: "multi-select",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "Payment amount",
        accessorKey: "paymentAmount",
        minSize: 250,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Payment Reference No",
        accessorKey: "paymentReferenceNumber",
        size: 300,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "New due date",
        accessorKey: "newDueDate",
        size: 350,
        filterVariant: "date-range",
        filterFn: (row, _id, filterValue) => {
          return datefilterFn(row.original.newDueDate, filterValue);
        },
        accessorFn: (row) => (row.newDueDate ? new Date(row.newDueDate) : null),
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="date" />,
      },
      {
        header: "Overdue",
        accessorKey: "overDueStatus",
        filterVariant: "multi-select",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "Last Payment Date",
        accessorKey: "lastPaymentDate",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="date" />,
        size: 350,
        filterVariant: "date-range",
        filterFn: (row, _id, filterValue) => {
          return datefilterFn(row.original.lastPaymentDate, filterValue);
        },
        accessorFn: (row) => (row.lastPaymentDate ? new Date(row.lastPaymentDate) : null),
      },
    ],
    []
  );

  const columnsPreviousPayments = useMemo<MRT_ColumnDef<EarlyPaymentData>[]>(
    () => [
      {
        header: "Vendor Name",
        accessorKey: "vendorName",
        minSize: 250,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "GRN Number",
        accessorKey: "grnNumber",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "Initiation Date",
        accessorKey: "initiationDate",
        size: 350,
        filterVariant: "date-range",
        filterFn: (row, _id, filterValue) => {
          return datefilterFn(row.original.initiationDate, filterValue);
        },
        accessorFn: (row) => (row.initiationDate ? new Date(row.initiationDate) : null),
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="date" />,
      },
      {
        header: "GRN Date",
        accessorKey: "grnDate",
        size: 350,
        filterVariant: "date-range",
        filterFn: (row, _id, filterValue) => {
          return datefilterFn(row.original.grnDate, filterValue);
        },
        accessorFn: (row) => (row.grnDate ? new Date(row.grnDate) : null),
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="date" />,
      },
      {
        header: "PAN",
        accessorKey: "pan",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "Amount disbursed",
        accessorKey: "amountDisbursed",
        minSize: 250,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Discount Amount",
        accessorKey: "discountAmount",
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Actual CN received (NAKAD)",
        accessorKey: "nakadCnReceived",
        filterVariant: "multi-select",
        size: 350,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "Actual CN received (Swiggy)",
        accessorKey: "anchorCnReceived",
        filterVariant: "multi-select",
        size: 350,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "Early Payment status",
        accessorKey: "earlyPaymentStatus",
        filterVariant: "multi-select",
        minSize: 300,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<EarlyPaymentStatus>()} type="text" />,
      },
      {
        header: "DN adjusted",
        accessorKey: "dnAdjusted",
        filterVariant: "multi-select",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "Vendor CN Date",
        accessorKey: "vendorCnDate",
        size: 350,
        filterVariant: "date-range",
        filterFn: (row, _id, filterValue) => {
          return datefilterFn(row.original.vendorCnDate, filterValue);
        },
        accessorFn: (row) => (row.vendorCnDate ? new Date(row.vendorCnDate) : null),
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="date" />,
      },

      {
        header: "Vendor CN Number",
        accessorKey: "vendorCnNumber",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "CN ageing status",
        accessorKey: "cnAgeingStatus",
        filterVariant: "multi-select",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "CN ageing",
        accessorKey: "cnAgeing",
        minSize: 250,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<number>()} type="number" />,
      },
      {
        header: "Income accrued to NAKAD",
        accessorKey: "incomeAccruedToNAKAD",
        size: 350,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<number>()} type="accounting" />,
      },
      {
        header: "Income accrued to Swiggy",
        accessorKey: "incomeAccruedToSwiggy",
        size: 350,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<number>()} type="accounting" />,
      },

      {
        header: "Early pay pass",
        accessorKey: "earlyPayPass",
        filterVariant: "multi-select",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },

      {
        header: "Early pay date",
        accessorKey: "earlyPayDate",
        size: 350,
        filterVariant: "date-range",
        filterFn: (row, _id, filterValue) => {
          return datefilterFn(row.original.earlyPayDate, filterValue);
        },
        accessorFn: (row) => (row.earlyPayDate ? new Date(row.earlyPayDate) : null),
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="date" />,
      },
      {
        header: "CN status",
        accessorKey: "cnStatus",
        size: 200,
        filterVariant: "multi-select",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "CN Number",
        accessorKey: "cnNumber",
        size: 200,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "Organization Name",
        accessorKey: "entityName",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },

      {
        header: "Warehouse Name",
        accessorKey: "warehouseName",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "Invoice Number",
        accessorKey: "invoiceNumber",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "Invoice Accounting Date",
        accessorKey: "invoiceAccountDate",
        size: 350,
        filterVariant: "date-range",
        filterFn: (row, _id, filterValue) => {
          return datefilterFn(row.original.invoiceAccountDate, filterValue);
        },
        accessorFn: (row) => (row.invoiceAccountDate ? new Date(row.invoiceAccountDate) : null),
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="date" />,
      },
      {
        header: "Invoice amount",
        accessorKey: "invoiceAmount",
        minSize: 250,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<number>()} type="accounting" />,
      },
      {
        header: "Outstanding payment",
        accessorKey: "outstandingAmount",
        minSize: 250,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<number>()} type="accounting" />,
      },
      {
        header: "Payment Status",
        accessorKey: "paymentStatus",
        filterVariant: "multi-select",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<PaymentStatus>()} type="text" />,
      },
      {
        header: "Vendor Code",
        accessorKey: "vendorCode",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "GSTIN",
        accessorKey: "gstin",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },

      {
        header: "City",
        accessorKey: "city",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "State",
        accessorKey: "state",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "Status of Invoice",
        accessorKey: "invoicePostedStatus",
        filterVariant: "multi-select",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "PO No.",
        accessorKey: "poNumber",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "PO Date",
        accessorKey: "poDate",
        size: 350,
        filterVariant: "date-range",
        filterFn: (row, _id, filterValue) => {
          return datefilterFn(row.original.poDate, filterValue);
        },
        accessorFn: (row) => (row.poDate ? new Date(row.poDate) : null),
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="date" />,
      },
      {
        header: "PO Amount",
        accessorKey: "poAmount",
        minSize: 250,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Creator Invoice Date",
        accessorKey: "creatorInvoiceDate",
        size: 250,
        accessorFn: (row) => (row.creatorInvoiceDate ? moment(row.creatorInvoiceDate).format("DD/MM/YYYY") : null),
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "GRN Amount",
        accessorKey: "grnAmount",
        minSize: 250,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "TDS/TCS",
        accessorKey: "tds",
        minSize: 250,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Purchase Return Amount",
        accessorKey: "purchaseReturnAmount",
        size: 300,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Brand discount (Promo Claims)",
        accessorKey: "brandDiscount",
        size: 350,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Other Debit Amount",
        accessorKey: "debitAmount",
        size: 300,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Other adjustments",
        accessorKey: "adjustments",
        size: 300,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Net Payable Amount",
        accessorKey: "netPayableAmount",
        size: 300,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Other Adjustment Date",
        accessorKey: "otherAdjustmentDate",
        size: 350,
        filterVariant: "date-range",
        filterFn: (row, _id, filterValue) => {
          return datefilterFn(row.original.otherAdjustmentDate, filterValue);
        },
        accessorFn: (row) => (row.otherAdjustmentDate ? new Date(row.otherAdjustmentDate) : null),
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="date" />,
      },
      {
        header: "Invoice Status",
        accessorKey: "invoiceReconciledStatus",
        filterVariant: "multi-select",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "Payment amount",
        accessorKey: "paymentAmount",
        minSize: 250,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Payment Reference No",
        accessorKey: "paymentReferenceNumber",
        size: 300,
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "Credit Period",
        accessorKey: "creditPeriod",
        minSize: 250,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="number" />,
      },
      {
        header: "Due Date",
        accessorKey: "dueDate",
        size: 350,
        filterVariant: "date-range",
        filterFn: (row, _id, filterValue) => {
          return datefilterFn(row.original.dueDate, filterValue);
        },
        accessorFn: (row) => (row.dueDate ? new Date(row.dueDate) : null),
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="date" />,
      },
      {
        header: "New due date",
        accessorKey: "newDueDate",
        size: 350,
        filterVariant: "date-range",
        filterFn: (row, _id, filterValue) => {
          return datefilterFn(row.original.newDueDate, filterValue);
        },
        accessorFn: (row) => (row.newDueDate ? new Date(row.newDueDate) : null),
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="date" />,
      },
      {
        header: "Overdue",
        accessorKey: "overDueStatus",
        filterVariant: "multi-select",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "Last Payment Date",
        accessorKey: "lastPaymentDate",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="date" />,
        size: 350,
        filterVariant: "date-range",
        filterFn: (row, _id, filterValue) => {
          return datefilterFn(row.original.lastPaymentDate, filterValue);
        },
        accessorFn: (row) => (row.lastPaymentDate ? new Date(row.lastPaymentDate) : null),
      },
      {
        header: "Adjustments",
        accessorKey: "finalAdjustments",
        minSize: 250,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Hold-off amt",
        accessorKey: "holdOffAmount",
        minSize: 250,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Outstanding (NAKAD)",
        accessorKey: "outstandingAmountNakad",
        minSize: 250,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Starting CB",
        accessorKey: "startingCB",
        minSize: 250,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Effective CB",
        accessorKey: "effectiveCB",
        minSize: 250,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Gross Payment Amt",
        accessorKey: "grossPaymentAmount",
        minSize: 250,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Balance amount to be paid on due date",
        accessorKey: "balanceAmountToBePaidOnDueDate",
        size: 420,
        filterVariant: "range",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="accounting" />,
      },
      {
        header: "Credit Note Number",
        accessorKey: "creditNoteNumber",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
      {
        header: "Discounting Status",
        accessorKey: "discountingStatus",
        filterVariant: "multi-select",
        Cell: ({ cell }) => <EllipsisedCell value={cell.getValue<string>()} type="text" />,
      },
    ],
    []
  );

  const dataByTab = convertHighlightToDataByTab(highlights);
  const cardCount = dataByTab.length;
  const mdSize = cardCount >= 5 ? 12 / 5 : 12 / cardCount;

  return (
    <LoggedInSkeleton
      topBarButtons={getReconTopBarButtons("Upload", actor.name, location?.state?.openCollapseOb, actor)}
    >
      <Box sx={{ bgcolor: "#fff", p: 2 }}>
        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          sx={{
            color: "custom.main", // your theme color
            "& .MuiTab-root": {
              color: "gray", // default color of tabs
              "&.Mui-selected": {
                color: "#541C4C", // your custom selected tab text color
              },
            },
            "& .MuiTabs-indicator": {
              backgroundColor: "#541C4C", // your custom indicator color
            },
          }}
        >
          {tabLabels.map((label, index) => (
            <Tab
              key={index}
              label={
                <Typography fontWeight={600} fontSize={16} textTransform={"none"}>
                  {label}
                </Typography>
              }
              sx={{ px: 3 }}
            />
          ))}
        </Tabs>
        <Divider />

        {/* <Stack direction="row" alignItems="center" spacing={1} m={2}>
          <Chip
            label="Last refreshed at 11:59 pm IST"
            sx={{
              color: "#0288D1",
              backgroundColor: "#E3F2FD",
              borderRadius: "4px",
            }}
          />
          <CustomButton onClick={() => console.log("Refresh data")} variant="text" startIcon={<RefreshIcon />}>
            Refresh data
          </CustomButton>
        </Stack> */}

        <Grid container spacing={2} mt={2} mb={3}>
          {dataByTab.map(({ title, value }, index) => (
            <Grid key={index} item xs={12} sm={6} md={mdSize}>
              <StatCard title={title} value={value} />
            </Grid>
          ))}
        </Grid>
        <Divider />
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 2 }}>
          <Box display="flex" justifyContent={"flex-start"} flexDirection={"row"} gap={2}>
            <Typography variant="h6" fontWeight={600}>
              Account Overview
            </Typography>
            {tabIndex === 1 && (
              <Chip
                label={"Current AUM: " + formatNumberAndCurrency(highlights.currentAUM, true)}
                sx={{
                  color: "#0288D1",
                  backgroundColor: "#E3F2FD",
                  borderRadius: "4px",
                  fontWeight: 600,
                  fontSize: "1rem",
                }}
              />
            )}
          </Box>
          <Stack direction="row" spacing={2}>
            <CustomButton
              variant="contained"
              endIcon={<ArrowDropDownIcon />}
              onClick={() => setBulkDialogOpen(true)}
              disabled={selectedRow.length === 0}
            >
              Bulk Actions
            </CustomButton>
            <CustomButton variant="outlined" startIcon={<FilterListIcon />} onClick={() => setOpenFilter(true)}>
              Filter
            </CustomButton>
            <CustomButton
              variant="outlined"
              startIcon={isDownloadingReport && <CircularProgress size={20} />}
              disabled={isDownloadingReport}
              onClick={() => handleDownloadReport()}
            >
              Download Report
            </CustomButton>
          </Stack>
        </Box>
        <Divider />
        <FiltersBars
          key={tabIndex}
          appliedFilters={tabIndex === 0 ? appliedFiltersDailyPayments : appliedFiltersPreviousPayments}
          columnFilters={tabIndex === 0 ? columnFiltersDailyPayments : columnFiltersPreviousPayments}
          setColumnFilters={tabIndex === 0 ? setColumnFiltersDailyPayments : setColumnFiltersPreviousPayments}
          setAppliedFilters={tabIndex === 0 ? setAppliedFiltersDailyPayments : setAppliedFiltersPreviousPayments}
        />
        <Stack direction="row" spacing={1} p={2}>
          {Object.values(InitiationDayFilter).map((label) => (
            <Chip
              key={label}
              label={capitalize(label)}
              variant={selectedTime === label ? "filled" : "outlined"}
              color="default" // keep this neutral, since we're overriding styles
              onClick={() => handleTimeFilter(label)}
              sx={{
                fontWeight: 500,
                backgroundColor: selectedTime === label ? "#EEE8ED" : "transparent",
                color: selectedTime === label ? "#541C4C" : "inherit",
                borderColor: selectedTime === label ? "#EEE8ED" : "#ccc", // optional for outlined
              }}
            />
          ))}
        </Stack>
        {tabIndex === 0 ? (
          <ReactTable
            key={"daily-payments"}
            data={earlyPaymentData}
            columns={columnsDailyPayments}
            isLoading={isLoading}
            enableRowSelection={true}
            setSelectedRow={setSelectedRow}
            clearRowsSelection={clearRows}
            setClearRowsSelection={setClearRows}
            enableRowVirtualization={true}
            enableColumnVirtualization={true}
            onColumnFiltersChange={setColumnFiltersDailyPayments}
            additionalState={{
              columnFilters: columnFiltersDailyPayments,
            }}
            additionalInitialState={{
              columnVisibility: {
                gstin: false,
                invoicePostedStatus: false,
                invoiceAmount: false,
                creditPeriod: false,
                balanceAmountToBePaidOnDueDate: false,
                vendorCode: false,
                city: false,
                state: false,
                poNumber: false,
                poDate: false,
                poAmount: false,
                tds: false,
                purchaseReturnAmount: false,
                brandDiscount: false,
                debitAmount: false,
                adjustments: false,
                netPayableAmount: false,
                otherAdjustmentDate: false,
                invoiceReconciledStatus: false,
                paymentAmount: false,
                paymentReferenceNumber: false,
                newDueDate: false,
                overDueStatus: false,
                lastPaymentDate: false,
              },
            }}
          />
        ) : (
          <ReactTable
            key={"early-payments"}
            data={earlyPaymentData}
            columns={columnsPreviousPayments}
            isLoading={isLoading}
            enableRowSelection={true}
            setSelectedRow={setSelectedRow}
            clearRowsSelection={clearRows}
            setClearRowsSelection={setClearRows}
            enableRowVirtualization={true}
            enableColumnVirtualization={true}
            onColumnFiltersChange={setColumnFiltersPreviousPayments}
            additionalState={{
              columnFilters: columnFiltersPreviousPayments,
            }}
            additionalInitialState={{
              columnPinning: { left: ["mrt-row-select"], right: ["mrt-row-actions"] },
              columnVisibility: {
                vendorCode: false,
                gstin: false,
                city: false,
                state: false,
                invoicePostedStatus: false,
                poNumber: false,
                poDate: false,
                poAmount: false,
                creatorInvoiceDate: false,
                grnAmount: false,
                tds: false,
                purchaseReturnAmount: false,
                brandDiscount: false,
                debitAmount: false,
                adjustments: false,
                netPayableAmount: false,
                otherAdjustmentDate: false,
                invoiceReconciledStatus: false,
                paymentAmount: false,
                paymentReferenceNumber: false,
                creditPeriod: false,
                dueDate: false,
                newDueDate: false,
                overDueStatus: false,
                lastPaymentDate: false,
                finalAdjustments: false,
                holdOffAmount: false,
                outstandingAmount: false,
                startingCB: false,
                effectiveCB: false,
                grossPaymentAmount: false,
                balanceAmountToBePaidOnDueDate: false,
                creditNoteNumber: false,
                discountingStatus: false,
                outstandingAmountNakad: false,
              },
            }}
            enableRowActions={true}
            displayColumnDefOptions={{
              "mrt-row-actions": {
                header: "Credit Note",
                size: 150,
              },
            }}
            renderRowActions={({ row }) => (
              <Box
                sx={{
                  display: "flex",
                  gap: 0.5,
                  justifyContent: "center",
                  width: "100%",
                }}
              >
                <CustomButton
                  variant="outlined"
                  size="small"
                  disabled={!row.original.isCreditNote}
                  onClick={() => {
                    handleDownloadCN(row.original.id);
                  }}
                >
                  Download
                </CustomButton>
              </Box>
            )}
          />
        )}
        {bulkDialogOpen && (
          <BulkActionsDialog
            open={bulkDialogOpen}
            onClose={() => setBulkDialogOpen(false)}
            selectedRowIds={selectedRow.map((row) => row.id)}
            getAllData={getDailyPaymentsData}
            clearSelectedRows={() => setClearRows(true)}
            useCase={tabIndex === 0 ? "dailyPayments" : "previousPayments"}
          />
        )}
        {openFilter && (
          <AnchorDashboardFilter
            open={openFilter}
            onClose={() => setOpenFilter(false)}
            setAppliedFilters={tabIndex === 0 ? setAppliedFiltersDailyPayments : setAppliedFiltersPreviousPayments}
            appliedFilters={tabIndex === 0 ? appliedFiltersDailyPayments : appliedFiltersPreviousPayments}
            useCase={tabIndex === 0 ? "dailyPayments" : "previousPayments"}
          />
        )}
      </Box>
    </LoggedInSkeleton>
  );
};

export default OperationsPage;
